import type * as highcharts from "highcharts";
import Highcharts from "highcharts";
import _ from "lodash";
import * as React from "react";
import { useQuery } from "react-query";
import { useLocation } from "react-router-dom";
import { LoadingDots } from "web-ui";

import {
  getMetricReportData,
  getMetricReportDataFast,
} from "api/dashboardBackend";
import type { TimestampedObservation } from "api/dashboardBackend";
import Chart, { ChartType } from "components/release/metric-chart/Chart";
import strings from "components/release/metric-chart/MetricChart.strings.json";
import {
  LIGHT_RED_COLOR,
  NEUTRAL_DARK_COLOR,
  getVersionColor,
} from "util/chart";
import { type PlatformId } from "util/platform";

// Confidence interval (i.e. error bar) type: [time, low, high]
type CIAtTime = [number, number, number];

const DAU_X_AXIS_START = 50;

export type IndependentVariableType = "time" | "dau";

interface SeriesOptionsType {
  type: ChartType;
  name: string;
  dashStyle?: string;
  data: ValueAtTime[] | CIAtTime[] | highcharts.Point[] | ValueDictionary[];
  visible: boolean;
  animation?: boolean | highcharts.AnimationOptionsObject;
  color?: string;
  categoryName?: string;
  enableMouseTracking?: boolean;
  stemWidth?: number;
  whiskerLength?: number;
  showInLegend?: boolean;
  lineWidth?: number;
  marker?: {
    enabled?: boolean;
  };
  id?: string;
}

interface ValueDictionary {
  x: number;
  y: number;
  custom?: Record<string, unknown>;
}

type ValueAtTime = [number, number];

const dateTimeFormat = "%b-%e %H:%M";

const getOptions = (
  metricName: string,
  series: SeriesOptionsType[],
  independentVariable: IndependentVariableType,
) => ({
  chart: { type: ChartType.LINE, zoomType: "xy" },
  legend: {
    align: "right",
    layout: "vertical",
    verticalAlign: "top",
  },
  series,
  title: { text: "" },
  tooltip: {
    formatter(this: Highcharts.TooltipFormatterContextObject) {
      const xAxisLabel =
        this.series.chart.xAxis[0].options.title?.text ?? "x axis";
      let formattedY;
      // Check if the series type is 'arearange'
      if (this.series.type === "arearange") {
        const low = Number(this.point.low).toPrecision(3);
        const high = Number(this.point.high).toPrecision(3);
        formattedY = `${low} - ${high}`; // Format for range
      } else {
        formattedY = Number(this.y).toPrecision(3); // Single value format
      }

      let formattedX;
      if (this.x !== undefined) {
        if (xAxisLabel === strings.time_title) {
          formattedX = Highcharts.dateFormat(dateTimeFormat, this.x as number);
        } else if (xAxisLabel === strings.dau_title) {
          formattedX = this.x.toLocaleString();
        }
      }

      const dot = `<span style="color: ${this.series.color};">&#9679;</span>`; // ● as a dot
      return `${xAxisLabel}: <b>${formattedX}</b></br> ${dot}${this.series.name}: <b>${formattedY}</b>`;
    },
  },
  xAxis:
    independentVariable === "time"
      ? {
          labels: { format: `{value:${dateTimeFormat}}` },
          title: { text: strings.time_title },
          type: "datetime",
        }
      : {
          labels: { format: undefined },
          title: { text: strings.dau_title },
          type: "logarithmic",
        },
  yAxis: {
    title: {
      text:
        metricName in strings
          ? strings[metricName as keyof typeof strings]
          : metricName,
    },
  },
});

const getSeriesFromData = (
  data: TimestampedObservation[],
  name: string,
  color: string,
  independentVariable: IndependentVariableType,
): SeriesOptionsType => {
  const predicate = (valueAtTime: ValueAtTime) =>
    independentVariable === "time" ? true : valueAtTime[0] > DAU_X_AXIS_START;
  const extractNumber = (target: number | string) =>
    typeof target === "string" ? parseFloat(target) : target;

  return {
    color,
    data: data
      .map<ValueAtTime>(observation => {
        const yValue = extractNumber(observation.value.readout);
        const xValue =
          independentVariable === "time"
            ? new Date(observation.timestamp).getTime()
            : extractNumber(observation.value.current_dau);
        return [xValue, yValue];
      })
      .filter(predicate),
    name,
    type: ChartType.LINE,
    visible: true,
  };
};

interface Props {
  independentVariable: IndependentVariableType;
  metricName: string;
  platformId: PlatformId;
  refId: string;
  version: string;
}

const MetricChart = ({
  independentVariable,
  metricName,
  platformId,
  refId,
  version,
}: Props) => {
  const { hash } = useLocation();

  const {
    data: dataSlow,
    isError: isErrorSlow,
    isLoading: isLoadingSlow,
  } = useQuery({
    queryFn: () => getMetricReportData(platformId, version, metricName),
    queryKey: [platformId, version, metricName],
  });
  const {
    data: dataFast,
    isError: isErrorFast,
    isLoading: isLoadingFast,
  } = useQuery({
    queryFn: () => getMetricReportDataFast(platformId, version, metricName),
    queryKey: [platformId, version, metricName, "fast"],
  });

  const data = dataSlow ?? dataFast;
  const isError = isErrorSlow && isErrorFast;
  const isLoading = isLoadingSlow && isLoadingFast;

  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    if (hash === `#${refId}`) {
      containerRef.current?.scrollIntoView();
    }
  }, [hash, refId]);

  if (isError) {
    return <div>Something went wrong</div>;
  }

  if (isLoading) {
    return <LoadingDots manualPositioning={true} type="screen-white" />;
  }

  if (!data) {
    return null;
  }

  const disallowAggregateBaseline = independentVariable !== "dau";
  const series = [];
  const currentVersionSegments = _.groupBy(
    data.metricValues,
    ({ value }) => value.version,
  );
  const currentVersions = _.keys(currentVersionSegments).sort((a, b) =>
    a.localeCompare(b),
  );
  currentVersions.forEach(fullVersion => {
    series.push(
      getSeriesFromData(
        currentVersionSegments[fullVersion],
        fullVersion,
        getVersionColor(platformId, fullVersion),
        independentVariable,
      ),
    );
  });

  if (data.baseline?.previous && data.baseline.previous.metricValues) {
    const versionString = data.baseline.previous.metricValues[0].value.version;
    series.push(
      getSeriesFromData(
        data.baseline.previous.metricValues,
        `Baseline (${versionString})`,
        NEUTRAL_DARK_COLOR,
        independentVariable,
      ),
    );
  }

  if (data.baseline?.aggregate && !disallowAggregateBaseline) {
    const sampleSize = data.baseline.aggregate.includedReleases.length;
    series.push({
      color: LIGHT_RED_COLOR,
      dashStyle: "LongDashDotDot",
      data: data.baseline.aggregate.summaryStatValues.map<CIAtTime>(
        ({ independentVariableValue, mean, stdDev }) => {
          const x =
            typeof independentVariableValue === "string"
              ? parseFloat(independentVariableValue)
              : independentVariableValue;
          const meanCasted = typeof mean === "string" ? parseFloat(mean) : mean;
          const stdDevCasted =
            typeof stdDev === "string" ? parseFloat(stdDev) : stdDev;
          return [
            x,
            Math.max(meanCasted - stdDevCasted * 2, 0),
            meanCasted + stdDevCasted * 2,
          ];
        },
      ),
      fillOpacity: 0.2,
      name: `μ ± 2σ (${sampleSize} release aggregate)`,
      type: ChartType.AREA_RANGE,
      visible: true,
    });
  }
  const options = getOptions(data.metricName, series, independentVariable);
  return (
    <div id={refId} ref={containerRef}>
      <Chart options={options as highcharts.Options} uniqueChartId={refId} />
    </div>
  );
};

export default MetricChart;
